import React, { useState } from 'react';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon, FilterIcon, DownloadIcon, PlayIcon, ArrowRightIcon, SettingsIcon, LayoutIcon, KanbanIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function OrchestrationJourneyMapPage() {
  const navigate = useNavigate();
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [timeWindow, setTimeWindow] = useState('30d');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [visibleLanes, setVisibleLanes] = useState({
    acquire: true,
    close: true,
    expand: true
  });
  // Stage configuration based on bow-tie architecture
  const stageConfig = {
    acquire: [{
      key: 'awareness',
      label: 'Awareness',
      order: 1,
      targetDays: 7,
      wipLimit: null
    }, {
      key: 'education',
      label: 'Education',
      order: 2,
      targetDays: 10,
      wipLimit: null
    }, {
      key: 'consideration',
      label: 'Consideration',
      order: 3,
      targetDays: 14,
      wipLimit: null
    }, {
      key: 'intent',
      label: 'Intent',
      order: 4,
      targetDays: 7,
      wipLimit: null
    }, {
      key: 'selection',
      label: 'Selection',
      order: 5,
      targetDays: 10,
      wipLimit: null
    }],
    close: [{
      key: 'evaluation',
      label: 'Evaluation',
      order: 1,
      targetDays: 21,
      wipLimit: 50
    }, {
      key: 'negotiation',
      label: 'Negotiation',
      order: 2,
      targetDays: 18,
      wipLimit: 30
    }, {
      key: 'commit',
      label: 'Commit',
      order: 3,
      targetDays: 12,
      wipLimit: 20
    }],
    expand: [{
      key: 'onboarding',
      label: 'Onboarding',
      order: 1,
      targetDays: 30,
      wipLimit: null
    }, {
      key: 'activation',
      label: 'Activation',
      order: 2,
      targetDays: 45,
      wipLimit: null
    }, {
      key: 'impact',
      label: 'Impact',
      order: 3,
      targetDays: 60,
      wipLimit: null
    }, {
      key: 'expansion',
      label: 'Expansion',
      order: 4,
      targetDays: 90,
      wipLimit: null
    }, {
      key: 'growth',
      label: 'Growth',
      order: 5,
      targetDays: 180,
      wipLimit: null
    }]
  };
  // Stage metrics with seeded data
  const stageMetrics = {
    awareness: {
      entries: 12450,
      exits: 9800,
      medianDays: 8,
      inFlightCount: 2650,
      health: 'healthy',
      activeProgramCount: 4,
      topOrchestrations: [{
        name: 'In-Market Takeover',
        last7dEntries: 342
      }, {
        name: 'High-Intent Blitz',
        last7dEntries: 189
      }]
    },
    education: {
      entries: 9800,
      exits: 7200,
      medianDays: 12,
      inFlightCount: 2600,
      health: 'healthy',
      activeProgramCount: 3,
      topOrchestrations: [{
        name: 'Content Nurture Series',
        last7dEntries: 234
      }, {
        name: 'Educational Webinar Flow',
        last7dEntries: 156
      }]
    },
    consideration: {
      entries: 7200,
      exits: 5340,
      medianDays: 16,
      inFlightCount: 1860,
      health: 'at-risk',
      activeProgramCount: 5,
      topOrchestrations: [{
        name: 'Product Demo Flow',
        last7dEntries: 234
      }, {
        name: 'Comparison Content',
        last7dEntries: 178
      }]
    },
    intent: {
      entries: 5340,
      exits: 3890,
      medianDays: 9,
      inFlightCount: 1450,
      health: 'healthy',
      activeProgramCount: 3,
      topOrchestrations: [{
        name: 'Trial Activation',
        last7dEntries: 189
      }, {
        name: 'Intent Signal Response',
        last7dEntries: 145
      }]
    },
    selection: {
      entries: 3890,
      exits: 2890,
      medianDays: 11,
      inFlightCount: 1000,
      health: 'healthy',
      activeProgramCount: 2,
      topOrchestrations: [{
        name: 'Vendor Selection Support',
        last7dEntries: 123
      }, {
        name: 'ROI Calculator Flow',
        last7dEntries: 98
      }]
    },
    evaluation: {
      entries: 2890,
      exits: 1450,
      medianDays: 23,
      inFlightCount: 1440,
      health: 'at-risk',
      activeProgramCount: 4,
      topOrchestrations: [{
        name: 'POV Setup',
        last7dEntries: 89
      }, {
        name: 'Executive Engagement',
        last7dEntries: 67
      }]
    },
    negotiation: {
      entries: 1450,
      exits: 890,
      medianDays: 24,
      inFlightCount: 560,
      health: 'critical',
      activeProgramCount: 3,
      topOrchestrations: [{
        name: 'Business Case Builder',
        last7dEntries: 45
      }, {
        name: 'Legal Review Acceleration',
        last7dEntries: 34
      }]
    },
    commit: {
      entries: 890,
      exits: 567,
      medianDays: 14,
      inFlightCount: 323,
      health: 'healthy',
      activeProgramCount: 2,
      topOrchestrations: [{
        name: 'Contract Acceleration',
        last7dEntries: 34
      }, {
        name: 'Signature Flow',
        last7dEntries: 28
      }]
    },
    onboarding: {
      entries: 567,
      exits: 523,
      medianDays: 32,
      inFlightCount: 44,
      health: 'healthy',
      activeProgramCount: 2,
      topOrchestrations: [{
        name: 'Welcome Series',
        last7dEntries: 156
      }, {
        name: 'Implementation Kickoff',
        last7dEntries: 89
      }]
    },
    activation: {
      entries: 523,
      exits: 478,
      medianDays: 48,
      inFlightCount: 45,
      health: 'healthy',
      activeProgramCount: 3,
      topOrchestrations: [{
        name: 'Feature Adoption',
        last7dEntries: 234
      }, {
        name: 'Usage Milestone Triggers',
        last7dEntries: 178
      }]
    },
    impact: {
      entries: 478,
      exits: 389,
      medianDays: 65,
      inFlightCount: 89,
      health: 'healthy',
      activeProgramCount: 2,
      topOrchestrations: [{
        name: 'Value Realization',
        last7dEntries: 123
      }, {
        name: 'QBR Preparation',
        last7dEntries: 98
      }]
    },
    expansion: {
      entries: 389,
      exits: 289,
      medianDays: 98,
      inFlightCount: 100,
      health: 'at-risk',
      activeProgramCount: 3,
      topOrchestrations: [{
        name: 'Upsell Triggers',
        last7dEntries: 89
      }, {
        name: 'Cross-sell Flow',
        last7dEntries: 67
      }]
    },
    growth: {
      entries: 289,
      exits: 245,
      medianDays: 195,
      inFlightCount: 44,
      health: 'healthy',
      activeProgramCount: 2,
      topOrchestrations: [{
        name: 'Strategic Account Planning',
        last7dEntries: 45
      }, {
        name: 'Executive Sponsor Program',
        last7dEntries: 34
      }]
    }
  };
  // Calculate hotspots
  const hotspots = Object.entries(stageMetrics).filter(([_, metrics]) => metrics.health === 'at-risk' || metrics.health === 'critical');
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'bg-green-100 text-green-700 border-green-300';
      case 'at-risk':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };
  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'healthy':
        return <CheckCircleIcon className="w-4 h-4" data-id="element-5227" />;
      case 'at-risk':
        return <AlertTriangleIcon className="w-4 h-4" data-id="element-5228" />;
      case 'critical':
        return <AlertTriangleIcon className="w-4 h-4" data-id="element-5229" />;
      default:
        return <CheckCircleIcon className="w-4 h-4" data-id="element-5230" />;
    }
  };
  const renderStageCard = (stageKey: string, stageLabel: string, laneColor: string) => {
    const metrics = stageMetrics[stageKey as keyof typeof stageMetrics];
    if (!metrics) return null;
    return <div key={stageKey} className={`bg-white rounded-lg border-2 p-4 cursor-pointer transition-all ${selectedStage === stageKey ? `border-${laneColor}-500 shadow-lg` : 'border-gray-200 hover:border-gray-300'}`} onClick={() => setSelectedStage(stageKey)} data-id="element-5231">
        <div className="flex items-center justify-between mb-3" data-id="element-5232">
          <h3 className="font-semibold text-gray-900" data-id="element-5233">{stageLabel}</h3>
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getHealthColor(metrics.health)}`} data-id="element-5234">
            {getHealthIcon(metrics.health)}
            {metrics.health}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3 text-center" data-id="element-5235">
          <div data-id="element-5236">
            <p className="text-xs text-gray-600" data-id="element-5237">Entries</p>
            <p className="text-lg font-bold text-gray-900" data-id="element-5238">
              {metrics.entries.toLocaleString()}
            </p>
          </div>
          <div data-id="element-5239">
            <p className="text-xs text-gray-600" data-id="element-5240">Exits</p>
            <p className="text-lg font-bold text-gray-900" data-id="element-5241">
              {metrics.exits.toLocaleString()}
            </p>
          </div>
          <div data-id="element-5242">
            <p className="text-xs text-gray-600" data-id="element-5243">Days</p>
            <p className="text-lg font-bold text-gray-900" data-id="element-5244">
              {metrics.medianDays}
            </p>
          </div>
        </div>
        <div className="space-y-2 mb-3" data-id="element-5245">
          {metrics.topOrchestrations.map(orch => <div key={orch.name} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200 hover:bg-blue-50 transition-colors" data-id="element-5246">
              <div className="flex items-center gap-2" data-id="element-5247">
                <PlayIcon className="w-3 h-3 text-blue-600" data-id="element-5248" />
                <span className="text-xs font-medium text-gray-900" data-id="element-5249">
                  {orch.name}
                </span>
              </div>
              <span className="text-xs text-gray-600" data-id="element-5250">
                {orch.last7dEntries}
              </span>
            </div>)}
        </div>
        <div className="flex items-center gap-2" data-id="element-5251">
          <button className="flex-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors" data-id="element-5252">
            View Runs
          </button>
          <button onClick={e => {
          e.stopPropagation();
          navigate('/orchestration/workflow');
        }} className="flex-1 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors" data-id="element-5253">
            Open Workflow
          </button>
        </div>
      </div>;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-5254">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-5255">
        <div className="flex items-center justify-between mb-4" data-id="element-5256">
          <div data-id="element-5257">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5258">Journey Map</h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-5259">
              End-to-end lifecycle view across Acquire, Close, and Expand
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-5260">
            <button onClick={() => navigate('/programs/kanban')} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5261">
              <LayoutIcon className="w-4 h-4" data-id="element-5262" />
              Kanban View
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5263">
              <FilterIcon className="w-4 h-4" data-id="element-5264" />
              Filters
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5265">
              <DownloadIcon className="w-4 h-4" data-id="element-5266" />
              Export
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5267">
              <SettingsIcon className="w-4 h-4" data-id="element-5268" />
              Configure
            </button>
          </div>
        </div>
        {/* Filters Row */}
        <div className="flex items-center gap-3 mb-4" data-id="element-5269">
          <select value={timeWindow} onChange={e => setTimeWindow(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-5270">
            <option value="7d" data-id="element-5271">Last 7 days</option>
            <option value="30d" data-id="element-5272">Last 30 days</option>
            <option value="90d" data-id="element-5273">Last 90 days</option>
            <option value="ytd" data-id="element-5274">Year to date</option>
          </select>
          <select value={segmentFilter} onChange={e => setSegmentFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-5275">
            <option value="all" data-id="element-5276">All Segments</option>
            <option value="enterprise" data-id="element-5277">Enterprise</option>
            <option value="mid-market" data-id="element-5278">Mid-Market</option>
            <option value="smb" data-id="element-5279">SMB</option>
          </select>
          <select value={programFilter} onChange={e => setProgramFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-5280">
            <option value="all" data-id="element-5281">All Programs</option>
            <option value="abm" data-id="element-5282">ABM Programs</option>
            <option value="plg" data-id="element-5283">PLG Programs</option>
            <option value="outbound" data-id="element-5284">Outbound Programs</option>
          </select>
        </div>
        {/* Hotspots Ribbon */}
        {hotspots.length > 0 && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-5285">
            <div className="flex items-center gap-2 mb-2" data-id="element-5286">
              <AlertTriangleIcon className="w-4 h-4 text-red-600" data-id="element-5287" />
              <span className="text-sm font-semibold text-red-900" data-id="element-5288">
                {hotspots.length} Hotspots Detected
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-5289">
              {hotspots.map(([stageKey, metrics]) => <button key={stageKey} onClick={() => setSelectedStage(stageKey)} className="px-3 py-1 bg-white border border-red-300 text-red-700 text-xs font-medium rounded hover:bg-red-50 transition-colors" data-id="element-5290">
                  {stageKey} ({metrics.health})
                </button>)}
            </div>
          </div>}
        {/* Summary Stats */}
        <div className="grid grid-cols-5 gap-4 mt-4" data-id="element-5291">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-id="element-5292">
            <p className="text-xs text-blue-700 font-medium mb-1" data-id="element-5293">
              Total Entries
            </p>
            <p className="text-2xl font-bold text-blue-900" data-id="element-5294">12,450</p>
          </div>
          <div className="p-4 bg-green-50 rounded-lg border border-green-200" data-id="element-5295">
            <p className="text-xs text-green-700 font-medium mb-1" data-id="element-5296">
              Active Programs
            </p>
            <p className="text-2xl font-bold text-green-900" data-id="element-5297">18</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200" data-id="element-5298">
            <p className="text-xs text-purple-700 font-medium mb-1" data-id="element-5299">
              Median Cycle
            </p>
            <p className="text-2xl font-bold text-purple-900" data-id="element-5300">127d</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200" data-id="element-5301">
            <p className="text-xs text-orange-700 font-medium mb-1" data-id="element-5302">
              Conversion Rate
            </p>
            <p className="text-2xl font-bold text-orange-900" data-id="element-5303">4.6%</p>
          </div>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200" data-id="element-5304">
            <p className="text-xs text-red-700 font-medium mb-1" data-id="element-5305">Hotspots</p>
            <p className="text-2xl font-bold text-red-900" data-id="element-5306">{hotspots.length}</p>
          </div>
        </div>
      </div>
      {/* Journey Lanes */}
      <div className="p-6" data-id="element-5307">
        <div className="grid grid-cols-3 gap-6" data-id="element-5308">
          {/* Acquire Lane */}
          {visibleLanes.acquire && <div className="space-y-4" data-id="element-5309">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 rounded-lg" data-id="element-5310">
                <h2 className="text-lg font-bold" data-id="element-5311">Acquire</h2>
                <p className="text-xs text-blue-100 mt-1" data-id="element-5312">
                  Awareness → Selection
                </p>
              </div>
              {stageConfig.acquire.map((stage, index) => <div key={stage.key} className="space-y-3" data-id="element-5313">
                  {renderStageCard(stage.key, stage.label, 'blue')}
                  {index < stageConfig.acquire.length - 1 && <div className="flex justify-center" data-id="element-5314">
                      <ArrowRightIcon className="w-5 h-5 text-gray-400 rotate-90" data-id="element-5315" />
                    </div>}
                </div>)}
            </div>}
          {/* Close Lane */}
          {visibleLanes.close && <div className="space-y-4" data-id="element-5316">
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-4 py-3 rounded-lg" data-id="element-5317">
                <h2 className="text-lg font-bold" data-id="element-5318">Close</h2>
                <p className="text-xs text-green-100 mt-1" data-id="element-5319">
                  Evaluation → Commit
                </p>
              </div>
              {stageConfig.close.map((stage, index) => <div key={stage.key} className="space-y-3" data-id="element-5320">
                  {renderStageCard(stage.key, stage.label, 'green')}
                  {index < stageConfig.close.length - 1 && <div className="flex justify-center" data-id="element-5321">
                      <ArrowRightIcon className="w-5 h-5 text-gray-400 rotate-90" data-id="element-5322" />
                    </div>}
                </div>)}
            </div>}
          {/* Expand Lane */}
          {visibleLanes.expand && <div className="space-y-4" data-id="element-5323">
              <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-3 rounded-lg" data-id="element-5324">
                <h2 className="text-lg font-bold" data-id="element-5325">Expand</h2>
                <p className="text-xs text-purple-100 mt-1" data-id="element-5326">
                  Onboarding → Growth
                </p>
              </div>
              {stageConfig.expand.map((stage, index) => <div key={stage.key} className="space-y-3" data-id="element-5327">
                  {renderStageCard(stage.key, stage.label, 'purple')}
                  {index < stageConfig.expand.length - 1 && <div className="flex justify-center" data-id="element-5328">
                      <ArrowRightIcon className="w-5 h-5 text-gray-400 rotate-90" data-id="element-5329" />
                    </div>}
                </div>)}
            </div>}
        </div>
      </div>
    </div>;
}